import React, { useState, useEffect } from "react";
import AdminSidebar from "../../../components/AdminSidebar";
import { axiosInstance } from "../../../utils/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import excelIcon from "../../../assets/excel-icon.webp";
import useDialogStore from "../../../stores/useDialogStore";
import ColumnSelector from "../../../components/ColumnSelector";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import ColumnSearch from "../../../components/ColumnSearch";
import Button from "../../../components/Button";
import PaginationV2 from "../../../components/PaginationV2";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import dayjs from "dayjs";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import { getUser } from "../../../utils/storage";

const InvoiceReport = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    model: true,
    brand: true,
    serial_no: true,
    counter_color: true,
    counter_black: true,
    total_counter: true,
    palette: true,
    created_at: true,
    options: true,
    comment: true,
    invoice_number: true,
    status: true,
    reserved_date: true,
    sold_date: true,
    client_id: true,
    client: true,
  });
  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "model", label: "Model" },
    { key: "brand", label: "Brand" },
    { key: "serial_no", label: "Serial No" },
    { key: "counter_color", label: "Counter Color" },
    { key: "counter_black", label: "Counter Black" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "created_at", label: "Created At" },
    { key: "options", label: "Device Options" },
    { key: "comment", label: "Comment" },
    { key: "invoice_number", label: "Invoice Number" },
    { key: "status", label: "Status" },
    { key: "reserved_date", label: "Reserved Date" },
    { key: "sold_date", label: "Sold Date" },
    { key: "client", label: "Client Name" },
    { key: "client_id", label: "Client ID" },
  ];
  const user = getUser();
  const { openDialog, closeDialog } = useDialogStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const sortConfig = {
    key: null,
    direction: "desc",
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Get invoice number from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const invoiceNumberFromUrl = urlParams.get("invoice_number");

  // Fetch invoice info using React Query
  const {
    data: invoiceInfo,
    isLoading: invoiceInfoLoading,
    refetch,
  } = useQuery({
    queryKey: ["invoiceInfo", invoiceNumberFromUrl, location.search],
    queryFn: async () => {
      if (!invoiceNumberFromUrl) return null;
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${
          window.REACT_APP_API_ENDPOINT
        }/device-statuses/by-invoice-number/${invoiceNumberFromUrl}?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!invoiceNumberFromUrl,
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["invoiceInfo"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["invoiceInfo"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname + "?invoice_number=" + invoiceNumberFromUrl);
    queryClient.invalidateQueries(["invoiceInfo"]);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setSearchQuery(searchQuery.trim());
      // Update URL with invoice number parameter
      const params = new URLSearchParams(location.search);
      params.set("invoice_number", searchQuery.trim());
      navigate(`${location.pathname}?${params.toString()}`);
    } else {
      toast.warning("Please enter an invoice number");
    }
  };

  // Update search query when URL parameter changes
  useEffect(() => {
    if (invoiceNumberFromUrl && invoiceNumberFromUrl !== searchQuery) {
      setSearchQuery(invoiceNumberFromUrl);
    }
  }, [invoiceNumberFromUrl]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/invoice-report`,
        {
          invoice_number: invoiceNumberFromUrl,
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob", // This tells Axios to expect a file
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `invoice_report_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up
      handleClearFilters();

      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleColumnToggle = (column) => {
    setSelectedColumns((prev) => {
      const newState = {
        ...prev,
        [column]: !prev[column],
      };
      return newState;
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns((prev) => {
      const allSelected = Object.values(prev).every((value) => value);
      const newState = Object.keys(prev).reduce(
        (acc, key) => ({ ...acc, [key]: !allSelected }),
        {}
      );
      return newState;
    });
  };

  const handleExport = (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const ColumnSelectorContent = () => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (
                Object.values(localSelectedColumns).some((selected) => selected)
              ) {
                setSelectedColumns(localSelectedColumns);
                handleExport(localSelectedColumns);
                closeDialog();
              } else {
                toast.warning("Please select at least one column to export");
              }
            }}
          >
            Export
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = () => {
    openDialog({
      title: "Select Columns to Export",
      content: <ColumnSelectorContent />,
    });
  };

  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["invoiceInfo"]);
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) => data.data.data.map(({ id, name }) => ({ id, name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id,
        name,
        device_brand_id,
      })),
  }).data;

  return (
    <AdminSidebar>
      <div className="space-y-6">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              Invoice Report
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              View and analyze devices related to specific invoices
            </p>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="space-y-4">
            <div className="max-w-sm">
              <label
                htmlFor="invoice-search"
                className="block text-sm font-medium text-gray-700"
              >
                Search Invoice
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                  <input
                    type="text"
                    name="invoice-search"
                    id="invoice-search"
                    className="block w-full rounded-none rounded-l-md border-0 py-1.5 pl-3 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Enter invoice number"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearch}
                  className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 bg-white"
                >
                  <MagnifyingGlassIcon
                    className="-ml-0.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              Invoice Report
            </h2>
            {invoiceInfoLoading ? (
              <p>Loading invoice information...</p>
            ) : invoiceInfo?.data?.data?.length > 0 ? (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Devices for Invoice #{invoiceInfo?.invoice_number}
                  </h3>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<XMarkIcon className="h-4 w-4" />}
                      onClick={handleClearFilters}
                      className="mb-2 lg:mb-0"
                    >
                      Clear Filters
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={handleOpenColumnSelector}
                      className="mb-2 lg:mb-0"
                      icon={excelIcon}
                    >
                      Download Invoice Report
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-black">
                      <tr>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Unique ID"
                              columnKey="unique_id"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Unique ID..."
                            />
                          </div>
                        </th>

                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Brand"
                              columnKey="brand"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Brand..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Model"
                              columnKey="model"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Model..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Serial No"
                              columnKey="serial_no"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Serial No..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Counter Color"
                              columnKey="counter_color"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Counter Color..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Counter Black"
                              columnKey="counter_black"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Counter Black..."
                            />
                          </div>
                        </th>

                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Total Counter"
                              columnKey="total_counter"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Total Counter..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Palette"
                              columnKey="palette"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Palette..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <ColumnSearch
                            columnName="Registered At"
                            columnKey="created_at"
                            isDate={true}
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Registered At..."
                          />
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          Options
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          Comment
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Status"
                              columnKey="status"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Status..."
                            />
                          </div>
                        </th>

                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Reserved Date"
                              columnKey="reserved_date"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              isDate={true}
                              placeholder="Search by Reserved Date..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Sold Date"
                              columnKey="sold_date"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              isDate={true}
                              placeholder="Search by Sold Date..."
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {invoiceInfoLoading ? (
                        <tr>
                          <td colSpan={9} className="text-center">
                            <LoadingComponent />
                          </td>
                        </tr>
                      ) : invoiceInfo?.data?.data?.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center">
                            <NoData message="No devices found for this invoice." />
                          </td>
                        </tr>
                      ) : (
                        invoiceInfo.data.data.map((item, index) => (
                          <tr
                            key={item.id}
                            className={`divide-y divide-gray-200 ${
                              index % 2 === 0 ? "bg-white" : "bg-gray-100"
                            }`}
                          >
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <div className="mt-1 text-black">
                                {item.unique_id}
                              </div>
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                              <div className="flex items-center pl-4">
                                {brands && (
                                  <EditableSelectCell
                                    value={item.device_brand_id?.toString()}
                                    displayValue={item.brand || ""}
                                    id={item.id}
                                    options={brands}
                                    field="brand"
                                    onSave={(data) => {
                                      editCellMutation.mutate({
                                        deviceId: data.id,
                                        field: "device_brand_id",
                                        value: data.value,
                                      });
                                    }}
                                  />
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {models && (
                                <EditableSelectCell
                                  value={item.device_model_id?.toString()}
                                  displayValue={item.model || ""}
                                  id={item.id}
                                  options={models.filter(
                                    (model) =>
                                      model.device_brand_id ===
                                      item.device_brand_id
                                  )}
                                  field="device_model_id"
                                  onSave={(data) =>
                                    editCellMutation.mutate({
                                      deviceId: data.id,
                                      field: "device_model_id",
                                      value: data.value,
                                    })
                                  }
                                />
                              )}
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={item.serial_no || "—"}
                                displayValue={item.serial_no || "—"}
                                id={item.id}
                                field="serial_no"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "serial_no",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={item.counter_color || "—"}
                                displayValue={item.counter_color || "—"}
                                id={item.id}
                                field="counter_color"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "counter_color",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={item.counter_black || "—"}
                                displayValue={item.counter_black || "—"}
                                id={item.id}
                                field="counter_black"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "counter_black",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={item.total_counter || "—"}
                                displayValue={item.total_counter || "—"}
                                id={item.id}
                                field="total_counter"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "total_counter",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={item.palette || "—"}
                                displayValue={item.palette || "—"}
                                id={item.id}
                                field="palette"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "palette",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>

                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {item.created_at
                                ? dayjs
                                    .utc(item.created_at)
                                    .format("DD-MM-YYYY")
                                : "—"}
                            </td>

                            <EditableOptionsCell
                              deviceId={item.id}
                              deviceOptions={item.device_options}
                            />

                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={item.comment || ""}
                                displayValue={item.comment || ""}
                                id={item.id}
                                field="comment"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "comment",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>

                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <span
                                className={`whitespace-nowrap uppercase p-3 text-sm text-black ${
                                  item.status === "sold"
                                    ? "text-green-500 bg-green-50 rounded-md px-2 py-1"
                                    : item.status === "reserved"
                                    ? "text-orange-500 bg-orange-50 rounded-md px-2 py-1"
                                    : "text-gray-500 bg-gray-50 rounded-md px-2 py-1"
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>

                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {item.reserved_date
                                ? dayjs
                                    .utc(item.reserved_date)
                                    .format("DD-MM-YYYY")
                                : "—"}
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {item.sold_date
                                ? dayjs.utc(item.sold_date).format("DD-MM-YYYY")
                                : "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationV2
                  current_page={invoiceInfo?.data?.current_page}
                  last_page={invoiceInfo?.data?.last_page}
                  next_page_url={invoiceInfo?.data?.next_page_url}
                  prev_page_url={invoiceInfo?.data?.prev_page_url}
                />
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">
                    Invoice Number:{" "}
                    <span className="font-bold">
                      {invoiceInfo?.invoice_number}
                    </span>
                  </p>
                  <p className="text-sm font-medium text-gray-700">
                    Client:{" "}
                    <span className="font-bold">
                      {invoiceInfo?.client_name}
                    </span>
                  </p>
                  <p className="text-sm font-medium text-gray-700">
                    Client Id:{" "}
                    <span className="font-bold">{invoiceInfo?.client_id}</span>
                  </p>
                </div>
              </div>
            ) : invoiceNumberFromUrl ? (
              <p>No information available for this invoice</p>
            ) : (
              <p>Enter an invoice number to view its information</p>
            )}
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
};

export default InvoiceReport;
